/* eslint-disable import/prefer-default-export */
import {
  computed,
  ref,
  unref,
  watch,
  type Ref,
  type UnwrapRef,
  type WatchStopHandle,
  toValue,
  toRef,
} from 'vue';
import { ValidationError } from 'yup';
import type { SchemaLike, ValidateOptions } from 'yup/lib/types';
import { isEqual } from 'lodash';
import type { MaybeRef } from '@vueuse/core';
import { setAt } from '@/utils/ds';

export interface ValidatedTree {
  [key: string]: ValidatedTree | string;
}

type FormError<T> = (UnwrapRef<T> extends Record<string, unknown> ? ValidatedTree : string) | undefined;

const isPromise = <T>(value: T | Promise<T>): value is Promise<T> => 'then' in value && 'catch' in value;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useValidation = <T>(schema: SchemaLike, data: T, options?: ValidateOptions<T>) => {
  const validatedData = ref<T>(unref(data));
  const errors = ref<FormError<T>>(undefined);
  let stop: WatchStopHandle | undefined;

  const validate = (newData: typeof data) => {
    try {
      validatedData.value = schema.validateSync(unref(newData), { abortEarly: false, ...options });
      errors.value = undefined;
    } catch (e: unknown) {
      (validatedData.value as T) = unref(newData);
      if (e instanceof ValidationError) {
        if (typeof unref(data) !== 'object') {
          (errors.value as string) = e.errors?.[0];
          return;
        }
        (errors.value as ValidatedTree) = {};
        for (const error of e.inner) {
          if (error.path) {
            setAt(errors.value as Record<string, string>, error.path.split('.'), error.message);
          }
        }
      }
    }
  };

  const setValidation = (active: boolean) => {
    if (stop) stop();
    if (active) {
      stop = watch(() => data, validate, { deep: true, immediate: true });
    } else {
      errors.value = undefined;
    }
  };

  const valid = computed(() => !errors.value);

  const validatedFn = <Return = unknown, Params extends Array<unknown> = []>(
    fn: (...args: Params) => Return | undefined,
  ) => (...args: Params) => {
      setValidation(true);
      if (!valid.value) return undefined;
      const result = fn(...args);

      if (result && isPromise(result)) {
        return result
          .catch((e) => {
            setValidation(false);
            throw e;
          })
          .then(() => {
            setValidation(false);
            return result;
          });
      }

      setValidation(false);
      return result;
    };

  return {
    data: validatedData,
    valid,
    errors,
    setValidation,
    validatedFn,
  };
};

export function useValidationExt<T>(schema: Ref<SchemaLike>, data: MaybeRef<T>, options?: ValidateOptions<T>) {
  const validatedData = ref<T>(toValue(data));
  const errors = ref<FormError<T>>(undefined) as Ref<FormError<T>>;
  let stop: WatchStopHandle | undefined;

  const validate = async (newValue: T) => {
    const value = newValue;
    try {
      validatedData.value = await schema.value.validate(value, {
        abortEarly: false,
        ...options,
      } as ValidateOptions);
      errors.value = undefined;
    } catch (e: unknown) {
      validatedData.value = value as UnwrapRef<T>;
      if (e instanceof ValidationError) {
        const errorObj: FormError<T> = {} as FormError<T>;
        e.inner.forEach((error) => {
          if (error.path) {
            setAt(errorObj as Record<string, any>, error.path.split('.'), error.message);
          }
        });
        errors.value = errorObj;
      } else {
        console.error('Unexpected validation error:', e);
      }
    }
  };
  const setValidation = (active: boolean) => {
    if (stop) stop();

    if (active) {
      const dataRef = toRef(data);
      stop = watch(dataRef, (value) => validate(value as T), { deep: true });
    } else {
      errors.value = undefined;
    }
  };

  const valid = computed(() => Object.keys(errors.value ?? {}).length === 0);

  const validatedFn = <Return = unknown, Params extends Array<unknown> = []>(
    fn: (...args: Params) => Return | undefined,
  ) => async (...args: Params) => {
      await validate(toValue(data));
      if (!valid.value) {
        console.warn('Validation failed:', errors.value);
        return undefined;
      }
      const result = fn(...args);
      if (isPromise(result)) {
        try {
          await result;
        } catch (e) {
          console.error('Function execution error:', e);
          throw e;
        }
      }
      return result;
    };

  return {
    data: validatedData,
    valid,
    errors,
    setValidation,
    validatedFn,
  };
}

export function useDirty<T, R>(value: T | Ref<T>, reference?: R | Ref<R>) {
  return computed(() => !isEqual(unref(value), unref(reference)));
}
